<template>
  <label
    class="jps-radio"
    ref="label"
    :disabled="disabled"
    @click="focus"
    @keydown.prevent.enter="click"
  >
    <input
      v-model="computedValue"
      type="radio"
      ref="input"
      :disabled="disabled"
      :required="required"
      :value="nativeValue" />
    <span class="check" />
    <span class="jps-radio-label">
      <slot />
    </span>
  </label>
</template>

<script lang="ts" setup>
interface Props {
  modelValue: string
  required?: boolean
  nativeValue?: string
  disabled?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  required: false,
  disabled: false
})

const emits = defineEmits(['update:modelValue'])

const newValue: Ref<string> = ref('')

const label = ref<HTMLElement>(null)
const input = ref<HTMLInputElement>(null)

const computedValue = computed({
  get: () => {
    return props.modelValue
  },
  set: (v) => {
    newValue.value = v
    emits('update:modelValue', v)
  },
})

const click = () => {
  label.value.click()
}

const focus = () => {
  input.value.focus()
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-radio {
  --radio-size: 1.25rem;
  display: inline-flex;
  align-items: center;
  margin-top: calc(0.25rem * 2);
  margin-bottom: calc(0.25rem * 2);
  display: flex;
  @include is-tablet {
    display: inline-flex;
  }

  input[type='radio'] {
    position: absolute;
    left: 0;
    opacity: 0;
    z-index: -1;
    + .check {
      display: flex;
      flex-shrink: 0;
      position: relative;
      cursor: pointer;
      width: var(--radio-size);
      height: var(--radio-size);
      transition: var(--transition);
      border-radius: 50%;
      border-width: 2px;
      border-color: $jps-gray-500;
      border-style: solid;
      margin-right: 0.25rem;
      &:before {
        content: '';
        display: flex;
        position: absolute;
        left: 50%;
        margin-left: calc(-1 * var(--radio-size) / 2);
        bottom: 50%;
        margin-bottom: calc(-1 * var(--radio-size) / 2);
        width: var(--radio-size);
        height: var(--radio-size);
        transition: var(--transition);
        border-radius: 50%;
        transform: scale(0);
        background-color: $jps-key-blue;
      }
    }
    &:checked + .check {
      border-color: $jps-key-blue;
      &:before {
        transform: scale(0.5);
      }
    }
    &:focus {
      + .check {
        box-shadow: 0 0 0.25em rgba(from theme('colors.gray.500') 1 1 1 / 0.8);
      }
      &:checked + .check {
        box-shadow: 0 0 0.25em
          rgba(from theme('colors.blue.DEFAULT') 1 1 1 / 0.8);
      }
    }
  }
  .jps-radio-label {
    cursor: pointer;
  }
  &:disabled {
    opacity: 0.5;
  }
  &:hover {
    input[type='radio']:not(:disabled) + .check {
      border-color: $jps-key-blue;
    }
  }
}
</style>
